/**
 * SPEC.md §8 — the interface theme toggle: its own control, deliberately
 * separate from `ThemeMenu.tsx`'s diagram-theme dropdown, not a shared
 * menu — picking a diagram theme and switching the app's own chrome
 * light/dark are unrelated actions (§8: "a user may well want dark chrome
 * and a light diagram for export"). Fully self-contained: unlike
 * diagramTheme/look, App.tsx never needs to know this exists (it never
 * affects the diagram or travels in share.ts's payload), so no props in,
 * no callback out.
 *
 * The actual toggle logic lives in `interfaceTheme.ts`'s
 * `toggleInterfaceTheme()` — M6.8 (issue #35)'s command palette calls the
 * exact same function for its "Toggle interface theme" entry, so this
 * button subscribes to that module's tiny store (`subscribeInterfaceThemeOverride`)
 * rather than owning the override in local state, or its own icon would go
 * stale the moment the palette (or a future third trigger) changes the
 * theme instead of this button.
 *
 * No persisted choice (`getInterfaceThemeOverride()` returns null) means
 * "follow the OS" — the `data-interface-theme` attribute stays off `<html>`
 * entirely so tokens.css's `@media (prefers-color-scheme)` rule keeps
 * deciding live. The first toggle always turns that into an explicit,
 * persisted override; this app never offers a third "back to system" state
 * once touched, matching most OS-following toggles.
 */
import { useEffect, useLayoutEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import {
  applyInterfaceTheme,
  getInterfaceThemeOverride,
  subscribeInterfaceThemeOverride,
  systemPrefersLight,
  toggleInterfaceTheme,
  type InterfaceTheme,
} from '../lib/interfaceTheme';

export default function InterfaceThemeToggle() {
  const [override, setOverride] = useState<InterfaceTheme | null>(getInterfaceThemeOverride);
  const [systemInterfaceTheme, setSystemInterfaceTheme] = useState<InterfaceTheme>(() =>
    systemPrefersLight() ? 'light' : 'dark',
  );
  const effectiveInterfaceTheme = override ?? systemInterfaceTheme;

  // Stays correct regardless of which UI (this button, or the command
  // palette's equivalent entry) actually called toggleInterfaceTheme().
  useEffect(() => subscribeInterfaceThemeOverride(setOverride), []);

  // Runs before the browser paints — a returning user whose stored override
  // differs from what the OS currently prefers would otherwise show a
  // flash of the wrong chrome for one frame.
  useLayoutEffect(() => {
    applyInterfaceTheme(override);
  }, [override]);

  // Only matters while there's no override — an explicit choice already
  // ignores the OS. Keeps this button's own icon honest if the OS
  // preference flips mid-session, the same live behavior tokens.css's media
  // query already gives the chrome itself.
  useEffect(() => {
    if (override !== null) return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    function onChange() {
      setSystemInterfaceTheme(mediaQuery.matches ? 'light' : 'dark');
    }
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, [override]);

  return (
    <button
      type="button"
      onClick={toggleInterfaceTheme}
      aria-label={
        effectiveInterfaceTheme === 'light' ? 'Switch to dark interface theme' : 'Switch to light interface theme'
      }
      className="flex items-center rounded-sm p-1.5 text-text-ink hover:bg-ink-3"
    >
      {effectiveInterfaceTheme === 'light' ? <Sun size={14} aria-hidden /> : <Moon size={14} aria-hidden />}
    </button>
  );
}
